import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';
import { reservationShortlistService } from '../service/reservationShortlist.service';

interface BookingHistoryProps {
  show: boolean;
  onHide: () => void;
}

interface BookingHistoryItem {
  id: number;
  firstName: string;
  lastName: string;
  mobileNumber: number;
  emailAddress: string;
  checkIn: string;
  checkOut: string;
  noOfDays: number;
  createdDate: string;
  bookedRooms: {
    id: number;
    priority: number;
    status: string; // Status from BookedRooms table
    room: {
      id: number;
      name: string;
      description?: string;
      area: number;
      roomType: {
        id: number;
        name: string;
        description?: string;
      };
      property: {
        id: number;
        name: string;
        description?: string;
        location: {
          id: number;
          name: string;
          zone: string;
          locationType: string;
          cityId: number;
        };
      };
      roomImages?: {
        id: number;
        roomId: number;
        imagePath: string;
      }[];
    };
  }[];
}

const BookingHistory: React.FC<BookingHistoryProps> = ({ show, onHide }) => {
  const [bookingHistory, setBookingHistory] = useState<BookingHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch booking history when modal opens
  useEffect(() => {
    if (show) {
      fetchBookingHistory();
    }
  }, [show]);

  const fetchBookingHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const items = await reservationShortlistService.getMyBookingHistory();
      console.log('Fetched booking history:', items);
      setBookingHistory(items);
    } catch (err) {
      setError('Failed to load booking history');
      console.error('Error fetching booking history:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to get status color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'reserved':
      case 'confirmed':
        return '#007bff'; // Blue for upcoming/confirmed
      case 'closed':
        return '#6c757d'; // Gray for completed/closed
      case 'cancelled':
        return '#dc3545'; // Red for cancelled
      default:
        return '#6c757d'; // Gray for unknown
    }
  };

  const getBookingStatus = (booking: BookingHistoryItem) => {
    // Get the most common status from all booked rooms in this booking
    if (!booking.bookedRooms || booking.bookedRooms.length === 0) {
      return { status: 'Unknown', color: '#6c757d' };
    }
    
    const statusCounts: { [key: string]: number } = {};
    booking.bookedRooms.forEach(room => {
      const status = room.status || 'Unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });
    
    // Get the most frequent status
    const mostCommonStatus = Object.keys(statusCounts).reduce((a, b) => 
      statusCounts[a] > statusCounts[b] ? a : b
    );
    
    return { 
      status: mostCommonStatus, 
      color: getStatusColor(mostCommonStatus) 
    };
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      centered
      backdrop="static"
    >
      <Modal.Header closeButton style={{ border: 'none', paddingBottom: '10px' }}>
        <Modal.Title style={{ fontSize: '24px', fontWeight: 'bold', color: '#333' }}>
          <Icon.ClockHistory className="me-2" />
          My Reservation History
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ padding: '0 24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>Loading your reservation history...</div>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#dc3545' }}>
            <div>{error}</div>
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={fetchBookingHistory}
              style={{ marginTop: '10px' }}
            >
              Try Again
            </Button>
          </div>
        ) : bookingHistory.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Icon.Calendar3 size={48} style={{ color: '#ccc', marginBottom: '15px' }} />
            <div style={{ color: '#666' }}>No booking history found</div>
            <div style={{ color: '#999', fontSize: '14px', marginTop: '5px' }}>
              Your completed bookings will appear here
            </div>
          </div>
        ) : (
          <>
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {bookingHistory.map((booking, index) => {
                const bookingStatusInfo = getBookingStatus(booking);
                
                return (
                  <div key={booking.id}>
                    <div style={{ 
                      padding: '16px 0',
                      borderBottom: index === bookingHistory.length - 1 ? 'none' : '1px solid #eee'
                    }}>
                      {/* Booking Header */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: '12px'
                      }}>
                        <div>
                          <h6 style={{ 
                            fontWeight: 'bold', 
                            margin: '0 0 4px 0',
                            fontSize: '16px',
                            color: '#333'
                          }}>
                            {booking.bookedRooms?.[0]?.room?.property?.name || 'Property Name'}
                          </h6>
                          <div style={{ 
                            fontSize: '14px',
                            color: '#666',
                            marginBottom: '4px'
                          }}>
                            {booking.bookedRooms?.[0]?.room?.property?.location?.name || 'Property Location'}
                          </div>
                          <div style={{ 
                            fontSize: '12px',
                            color: '#999'
                          }}>
                            Booking ID: #{booking.id} | Booked on: {formatDate(booking.createdDate)}
                          </div>
                        </div>
                        
                        <div style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          backgroundColor: bookingStatusInfo.color,
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          {bookingStatusInfo.status}
                        </div>
                      </div>

                      {/* Booking Details */}
                      <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap',
                        gap: '20px',
                        marginBottom: '12px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Icon.CalendarCheck size={14} style={{ color: '#007bff' }} />
                          <span style={{ fontSize: '14px', color: '#666' }}>
                            Check-in: {formatDate(booking.checkIn)}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Icon.CalendarX size={14} style={{ color: '#dc3545' }} />
                          <span style={{ fontSize: '14px', color: '#666' }}>
                            Check-out: {formatDate(booking.checkOut)}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Icon.Clock size={14} style={{ color: '#28a745' }} />
                          <span style={{ fontSize: '14px', color: '#666' }}>
                            {booking.noOfDays} {booking.noOfDays === 1 ? 'night' : 'nights'}
                          </span>
                        </div>
                      </div>

                      {/* Guest Details */}
                      <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap',
                        gap: '20px',
                        marginBottom: '12px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Icon.Person size={14} style={{ color: '#6c757d' }} />
                          <span style={{ fontSize: '14px', color: '#666' }}>
                            {booking.firstName} {booking.lastName}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Icon.Telephone size={14} style={{ color: '#6c757d' }} />
                          <span style={{ fontSize: '14px', color: '#666' }}>
                            {booking.mobileNumber}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Icon.Envelope size={14} style={{ color: '#6c757d' }} />
                          <span style={{ fontSize: '14px', color: '#666' }}>
                            {booking.emailAddress}
                          </span>
                        </div>
                      </div>

                      {/* Booked Rooms */}
                      {booking.bookedRooms && booking.bookedRooms.length > 0 && (
                        <div style={{ 
                          backgroundColor: '#f8f9fa',
                          borderRadius: '6px',
                          padding: '12px',
                          marginTop: '8px'
                        }}>
                          <div style={{ 
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#333',
                            marginBottom: '8px'
                          }}>
                            Reserved Rooms ({booking.bookedRooms.length})
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                            {booking.bookedRooms.map((bookedRoom, roomIndex) => (
                              <div key={bookedRoom.id} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '8px',
                                backgroundColor: 'white',
                                borderRadius: '6px',
                                border: '1px solid #e9ecef',
                                minWidth: '250px'
                              }}>
                                {/* Room Image Thumbnail */}
                                <div style={{
                                  width: '50px',
                                  height: '50px',
                                  borderRadius: '4px',
                                  overflow: 'hidden',
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  flexShrink: 0
                                }}>
                                  {bookedRoom.room?.roomImages && bookedRoom.room.roomImages.length > 0 ? (
                                    <img 
                                      src={bookedRoom.room.roomImages[0].imagePath} 
                                      alt={`${bookedRoom.room?.name} thumbnail`}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                      }}
                                      onError={(e) => {
                                        // Fallback to icon if image fails to load
                                        const target = e.target as HTMLImageElement;
                                        target.style.display = 'none';
                                        target.parentElement!.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #6c757d;"><svg width="24" height="24" fill="currentColor" viewBox="0 0 16 16"><path d="M8.5 10c0 .5-.5 1-1 1s-1-.5-1-1 .5-1 1-1 1 .5 1 1z"/><path d="M2 1a1 1 0 0 0-1 1v9.5a1 1 0 0 0 1 1h2.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H2zm7.5 0a1 1 0 0 0-1 1v9.5a1 1 0 0 0 1 1H12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9.5z"/></svg></div>';
                                      }}
                                    />
                                  ) : (
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      height: '100%',
                                      color: '#6c757d'
                                    }}>
                                      <Icon.DoorOpen size={24} />
                                    </div>
                                  )}
                                </div>

                                {/* Room Details */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ 
                                    fontSize: '13px', 
                                    color: '#333',
                                    fontWeight: '500',
                                    marginBottom: '2px'
                                  }}>
                                    {bookedRoom.room?.name || `Room ${bookedRoom.room?.id}`}
                                  </div>
                                  {bookedRoom.room?.roomType?.name && (
                                    <div style={{ 
                                      fontSize: '12px', 
                                      color: '#6c757d',
                                      marginBottom: '4px'
                                    }}>
                                      {bookedRoom.room.roomType.name}
                                    </div>
                                  )}
                                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    {bookedRoom.priority && (
                                      <span style={{
                                        fontSize: '10px',
                                        padding: '2px 6px',
                                        backgroundColor: '#007bff',
                                        color: 'white',
                                        borderRadius: '10px',
                                        fontWeight: 'bold'
                                      }}>
                                        P{bookedRoom.priority}
                                      </span>
                                    )}
                                    <span style={{
                                      fontSize: '10px',
                                      padding: '2px 6px',
                                      backgroundColor: getStatusColor(bookedRoom.status || 'Unknown'),
                                      color: 'white',
                                      borderRadius: '10px',
                                      fontWeight: 'bold'
                                    }}>
                                      {bookedRoom.status || 'Unknown'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Summary Section */}
            <div style={{ 
              borderTop: '1px solid #eee',
              paddingTop: '16px',
              marginTop: '16px'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ 
                  color: '#666',
                  fontSize: '16px'
                }}>
                  Total Bookings
                </div>
                <div style={{ 
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  {bookingHistory.length}
                </div>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default React.memo(BookingHistory);